import React, { lazy, Suspense } from 'react'
import { appConfig } from '@fs/zion-config'
import { useFeatureFlag } from '@fs/zion-flags'
import { NoticeLoading } from '@fs/zion-icon'
import Head from '@fs/zion-head'
import { Switch, Route, NotFound, AuthRoute, Redirect } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import CenterAdminProvider from './hooks/CenterAdminProvider'
import NavigationTabs from './components/locationTools/NavigationTabs'
import ErrorLoadingHandler from './components/locationTools/ErrorPages/ErrorLoadingHandler'

const LocationsHomePage = lazy(() => import('./components/home/LocationsHomePage'))
const AffiliatesAboutPage = lazy(() => import('./components/affiliates/about/AboutPage'))
const AffiliateHomePage = lazy(() => import('./components/affiliates/home/AffiliateHomePage'))
const AffiliateOnboardingPage = lazy(() => import('./components/affiliates/onboarding/OnboardingPage'))
const MyLocations = lazy(() => import('./components/affiliates/locations/MyLocations'))
const AffiliatesAdmin = lazy(() => import('./components/affiliates/admin/AffiliatesAdmin'))
const CentersPage = lazy(() => import('./components/centers/CentersPage'))
const ZCMSPages = lazy(() => import('./components/affiliates/ZCMSPages'))

const LocationPage = React.lazy(() => import('./components/locationTools/AdminPage/AdminPage/AdminPage'))
const PrinterAdminPage = React.lazy(() => import('./components/locationTools/PrinterAdminPage/PrinterAdminPage'))
const ResourcesPage = React.lazy(() => import('./components/locationTools/ResourcePage/ResourcePage'))
const SearchPage = React.lazy(() => import('./components/locationTools/SearchPage/SearchPage'))
const SelectedCenter = React.lazy(() => import('./components/locationTools/SearchPage/SelectedCenter'))
const Announcements = React.lazy(() => import('./components/locationTools/Announcements/Announcements'))
const CenterCountsPage = React.lazy(() => import('./components/locationTools/CenterCountsPage/CenterCountsPage'))
const FacilityReportsPage = React.lazy(
  () => import('./components/locationTools/FacilityReportsPage/FacilityReportsPage')
)

const PatronGuideV1 = lazy(() => import('./components/patron-guide/PatronGuideV1'))
const PatronGuideV2 = lazy(() => import('./components/patron-guide/PatronGuideV2'))

function App() {
  const affiliateProgramOn = useFeatureFlag('help_affiliate_program').isOn
  const pathname = window.location.pathname
  const paths = [
    '/search',
    '/mycenters',
    '/admin',
    '/resources',
    '/center',
    '/announcements',
    '/center-counts',
    '/custom-center-reports',
  ]
  const queryClient = new QueryClient()
  return (
    <>
      <Head>
        <link rel="preconnect" href={appConfig.BRIGHTSPOT_URL} />
        <link rel="preconnect" href="https://assets.ldscdn.org" />
        <link rel="preconnect" href="https://foundry.familysearch.org" />
        <link rel="preconnect" href="https://consent.trustarc.com" />
      </Head>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<NoticeLoading />}>
            <Switch>
              {pathname.endsWith('/locations') && <Route component={LocationsHomePage} />}
              <AuthRoute exact path={paths}>
                <ErrorBoundary FallbackComponent={ErrorLoadingHandler}>
                  <CenterAdminProvider>
                    <NavigationTabs />
                    <Route path="/mycenters" component={LocationPage} />
                    <Route path="/search" component={SearchPage} />
                    <Route path="/admin" component={PrinterAdminPage} />
                    <Route path="/resources" component={ResourcesPage} />
                    <Route path="/center" component={SelectedCenter} />
                    <Route path="/announcements" component={Announcements} />
                    <Route path="/center-counts" component={CenterCountsPage} />
                    <Route path="/custom-center-reports" component={FacilityReportsPage} />
                  </CenterAdminProvider>
                </ErrorBoundary>
              </AuthRoute>
              <Route path="/about" component={LocationsHomePage} exact />
              <Route path="/affiliates" component={AffiliatesAboutPage} exact />
              <Route path="/affiliates/about" component={AffiliatesAboutPage} exact />
              <Route path="/affiliates/home" component={AffiliateHomePage} exact />
              {affiliateProgramOn && <AuthRoute path="/affiliates/my-locations" component={MyLocations} />}
              {affiliateProgramOn && <AuthRoute path="/affiliates/admin" component={AffiliatesAdmin} />}
              {affiliateProgramOn && <Route path="/affiliates/onboarding" component={AffiliateOnboardingPage} />}
              <Route path="/affiliates/*" component={ZCMSPages} />
              <Route path="/centers" component={CentersPage} exact />
              <Route path="/centers/about" component={CentersPage} exact />
              <Route path="/library/patron-guide/v1" component={PatronGuideV1} />
              <Route path="/library/patron-guide/v2" component={PatronGuideV2} />

              <Redirect from="/" to="/about" exact />

              {/* blank page for performance testing purposes only */}
              <Route path="/blank" component={() => 'This page is intentionally left blank'} />

              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </QueryClientProvider>
      </ErrorBoundary>
    </>
  )
}
export default App
